/* eslint-disable max-lines */
import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { OrgUserStatus } from '@api';
import { buildCognitoLogoutUrl } from '@auth/common/cognitoLogout';
import { Region } from '@components/common/constants';
import { isRudderUser } from '@components/common/util/util';
import { Analytics, webapp } from '@lib/analytics';
import type { webappTypes } from '@lib/analytics';
import { bugsnagSetUser, webappClient } from '@lib/bugsnag';
import { experiment } from '@lib/experiment';
import { loadSchedulerWidget } from '@lib/scheduler';
import { apiAuthCaller } from '@services/apiCaller';
import auth, { CognitoTokens, MfaMethod, OrgOption } from '@services/cognito';
import { amplitudeEngagement } from '@services/amplitudeEngagement';
import { customerIoInApp } from '@services/customerIoInApp';
import {
  announceSharedSignOut,
  sharedSession,
  SharedSessionSignInError,
  UNTRUSTED_SITE_MESSAGE,
} from '@services/sharedSession';
import { IRootStore } from '@stores/index';
import { WorkspaceEnvironment } from '@stores/workspace';
import { ACCESSIBLE_WORKSPACE_STATUSES, WorkspaceStatus } from '@stores/workspace/constants';
import LocalStorage from '@utils/localStorage';
import { CatchErr } from '@utils/types';

import { OrganizationRole, UserRoles, WorkspaceRole } from '../member';
import type { Traits, TraitsV2 } from './types';

export enum LoginMfaState {
  None = '',
  ForcedSetup = 'mfa_setup',
  Challenge = 'mfa_challenge',
  GracePeriod = 'mfa_grace_period',
}
export enum PendingTask {
  databricks = 'DATABRICKS_ONBOARDING',
}
export type Provider = 'Google' | '';
/** `shared`: the tokens come from the shared session of the web app and Lookout (ACT2-879), in memory only. */
type SessionMode = 'stored' | 'shared';
type MFAStatus = 'SMS' | 'TOTP' | 'NOMFA';
interface IMfaData {
  sessionToken?: string;
  status?: string;
  email?: string;
  provider?: string;
  providerUserId?: string;
  state?: LoginMfaState;
  mfaMethod?: MfaMethod;
  availableMethods?: MfaMethod[];
  gracePeriodDeadline?: string;
}
interface IUserData {
  email: string;
  id: string;
  name: string;
  provider: Provider;
  providerUserId: string;
  workspaces: IWorkspace[];
  phoneNumber: string;
  mfaStatus: MFAStatus;
  enforcedMfa: boolean;
  hasTotpEnabled: boolean;
  canSetupMfa: boolean;
  organizations: IOrganization[];
  pendingTasks: PendingTask[];
  createdAt: string;
  updatedAt: string;
}
interface IOrganization {
  id: string;
  name: string;
  role: OrganizationRole;
  planType: string;
  status?: OrgUserStatus;
}

export type { Traits, TraitsV2 } from './types';
interface IWorkspace {
  id: string;
  name: string;
  organizationId: string;
  environment: WorkspaceEnvironment;
  roles: WorkspaceRole[];
  defaultRegion: Region;
  status: WorkspaceStatus;
  v2WorkspaceAccess: boolean;
}

/**
 * - `ready`: the routes can render, with or without a session.
 * - `unavailable`: config-backend did not answer, and a session may still live there. Nobody is signed out.
 */
export type SessionStart = 'ready' | 'unavailable';

/** Amplify's keys in localStorage: the Cognito tokens of a Google or SSO sign-in, and its Hosted UI flags. */
const removeAmplifyTokens = () => {
  try {
    const { localStorage } = window;
    // The page reads all keys first, because each removal moves the index of the next keys.
    Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter(
        (key): key is string =>
          !!key &&
          (key.startsWith('CognitoIdentityServiceProvider.') ||
            key === 'amplify-signin-with-hostedUI' ||
            key === 'amplify-redirected-from-hosted-ui'),
      )
      .forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    // A browser can block localStorage. Then Amplify also kept no tokens, and the sign-out continues.
  }
};

export interface IUserStore {
  setUserSessionForMFA(email: string, mfaData: IMfaData): void;
  setAndRefreshToken(token: string, minValiditySeconds?: number): Promise<void>;
  getTokenExpirationTime(): number | null;
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  token: string;
  loggedIn: boolean;
  invitedUserFirstTimeLogin: boolean;
  firstTimeLogin: boolean;
  sessionData: CognitoTokens;
  workspaces: Array<IWorkspace>;
  selectedWorkspaceId: string;
  selectedOrganizationId: string;
  isOrgAdmin: boolean;
  roles: UserRoles;
  rootStore: IRootStore;
  provider: Provider;
  providerUserId: string;
  mfaData: IMfaData | undefined;
  phoneNumber: string;
  mfaStatus: MFAStatus;
  enforcedMfa: boolean;
  hasTotpEnabled: boolean;
  canSetupMfa: boolean;
  signUp(name: string, organization: string, email?: string): void;
  setName(name: string): void;
  /* called after the actual login to fetch user details */
  loginUser(sessionData: CognitoTokens): Promise<void>;
  loginWithFederatedTokens(tokens: CognitoTokens): Promise<void>;
  sessionMode: SessionMode;
  /** The start ended a stored session, because the page switched to the shared session (ACT2-879). */
  signInAgainAfterSwitch: boolean;
  startSession(): Promise<SessionStart>;
  endSessionFromAnotherTab(): Promise<void>;
  trackOnboarding(traits: Traits): void;
  trackOnboardingV2(traits: TraitsV2): void;
  identifyFoundation(extra?: Partial<webappTypes.IdentifyTraits>): void;
  setLoginMetadata(metadata: { invited?: boolean; firstTimeLogin?: boolean }): void;
  setProvider(provider: Provider, providerUserId: string): void;
  signOutUser(signOutfromBackend?: boolean, globalSignout?: boolean): Promise<void>;
  signOutUserAndClearHostedUiSession(): Promise<void>;
  signOutFromSessionErrorPage(): Promise<void>;
  isFederatedLogin: boolean;
  switchWorkspace(workspaceId: string): Promise<void>;
  updateEnforcedMfa(): Promise<void>;
  setPhoneNumber(number: string): void;
  setMfaStatus(value: MFAStatus): void;
  /** Loads the signed-in user. Resolves false when `/getUser` failed. */
  getUser(): Promise<boolean>;
  isSso: boolean;
  isGoogleOrSso: boolean;
  organizations: IOrganization[];
  accessibleOrganizations: IOrganization[];
  pendingTasks: PendingTask[];
  showDatabricksWorkspace: boolean;
  setShowDatabricksWorkspace(val: boolean): void;
  removePendingTask(task: PendingTask): void;
  selectedWorkspaceRegion: Region;
  selectedWorkspace: IWorkspace | undefined;
  selectedOrganization: IOrganization | undefined;
  isUserSuspendedInSelectedOrganization: boolean;
  accessibleWorkspaces: IWorkspace[];
  ensureProductionWorkspaceSelected(): boolean;
  showGetStarted: boolean;
  setShowGetStarted(val: boolean): void;
  ssoOrgOptions: OrgOption[];
  setSsoOrgOptions(options: OrgOption[]): void;
  clearSsoOrgOptions(): void;
}

type LocalUserStore = Pick<
  UserStore,
  'token' | 'loggedIn' | 'email' | 'provider' | 'selectedWorkspaceId'
> & {
  refreshToken: UserStore['sessionData']['refreshToken'];
  sessionMode?: SessionMode;
};

function autoSave(store: UserStore) {
  let firstRun = true;

  autorun(() => {
    const userStore = toJS(store);

    if (!firstRun) {
      // The shared session keeps its tokens in memory only.
      const shared = userStore.sessionMode === 'shared';
      const json: LocalUserStore = {
        refreshToken: shared ? '' : userStore.sessionData.refreshToken,
        loggedIn: userStore.loggedIn,
        token: shared ? '' : userStore.token,
        email: userStore.email,
        provider: userStore.provider,
        // workspaceId is required in destination oauth callback route to make API call
        selectedWorkspaceId: userStore.selectedWorkspaceId,
        sessionMode: userStore.sessionMode,
      };
      LocalStorage.set('userStore', JSON.stringify(json));
    }
    firstRun = false;
  });
}

/** Decodes a JWT's claims, or null when the token isn't a decodable JWT. */
const decodeTokenClaims = (token: string): Record<string, unknown> | null => {
  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }
  try {
    // JWT payloads are base64url-encoded; atob only accepts plain base64.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

// Local-mode tokens carry no `sub`, so the email names the account.
const emailOf = (idToken: string) => {
  const email = decodeTokenClaims(idToken)?.email;
  return typeof email === 'string' ? email.toLowerCase() : undefined;
};

/** True when both ID tokens name the same account. */
const isSameAccount = (idToken: string, otherIdToken: string) => {
  const email = emailOf(idToken);
  return email !== undefined && email === emailOf(otherIdToken);
};

/** True when the ID token names an account other than `email`. */
const isOtherAccount = (idToken: string, email: string) => {
  const tokenEmail = emailOf(idToken);
  return !!tokenEmail && !!email && tokenEmail !== email.toLowerCase();
};

/** How long the page waits before it asks a shared session that did not answer at start again. */
const SESSION_START_RETRY_MS = 1000;
/** The routes wait for these calls, so they time out sooner than other calls. */
const SESSION_START_TIMEOUT_MS = 10000;
/** A Log out does not wait long for a call that only might end a shared session. */
const BEST_EFFORT_SIGN_OUT_TIMEOUT_MS = 5000;

const SHARED_SESSION_UNAVAILABLE_MESSAGE = 'Could not reach RudderStack. Try again.';
const SHARED_SESSION_COOKIE_MESSAGE =
  'This browser did not keep the RudderStack session cookie. Allow cookies for this site and try again.';

const sharedSignInFailureMessage = (kind: string) => {
  if (kind === 'unavailable') {
    return SHARED_SESSION_UNAVAILABLE_MESSAGE;
  }
  if (kind === 'refused') {
    return UNTRUSTED_SITE_MESSAGE;
  }
  return SHARED_SESSION_COOKIE_MESSAGE;
};

export class UserStore implements IUserStore {
  @observable public id = '';

  @observable public name = '';

  @observable public email = '';

  @observable public createdAt = '';

  @observable public updatedAt = '';

  @observable public token = '';

  @observable public sessionData: CognitoTokens = {
    idToken: '',
    refreshToken: '',
  };

  @observable public loggedIn = false;

  @observable public sessionMode: SessionMode = 'stored';

  @observable public signInAgainAfterSwitch = false;

  @observable public invitedUserFirstTimeLogin = false;

  @observable public firstTimeLogin = false;

  @observable enforcedMfa = false;

  @observable public workspaces: IWorkspace[] = [];

  @observable public organizations: IOrganization[] = [];

  @observable public selectedWorkspaceId = '';

  @observable public rootStore: IRootStore;

  @observable public provider: Provider = '';

  @observable public providerUserId = '';

  @observable public mfaData: IMfaData | undefined;

  @observable phoneNumber = '';

  @observable mfaStatus: MFAStatus = 'NOMFA';

  @observable canSetupMfa = false;

  @observable pendingTasks: PendingTask[] = [];

  @observable showDatabricksWorkspace = true;

  @observable showGetStarted = false;

  @observable public ssoOrgOptions: OrgOption[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.load();
    autoSave(this);
    this.rootStore = rootStore;
  }

  public load() {
    const userStore = LocalStorage.get('userStore');
    if (userStore) {
      const store: LocalUserStore = JSON.parse(userStore);
      this.sessionData = { refreshToken: store.refreshToken, idToken: store.token };
      this.token = store.token;
      this.loggedIn = store.loggedIn;
      this.email = store.email;
      this.selectedWorkspaceId = store.selectedWorkspaceId;
      this.provider = store.provider;
      this.sessionMode = store.sessionMode === 'shared' ? 'shared' : 'stored';
    }
  }

  /**
   * Starts the page's session before any route renders. With the shared session (ACT2-879), a session that
   * config-backend has for this browser signs the user in, also after a sign-in on Lookout. Any other session ends,
   * also a stored session from before the shared session, so the user signs in again. When config-backend does not
   * answer, the session stays, and the page asks for a reload. This also applies when this page has no session of its
   * own, because only config-backend knows a session that started on Lookout.
   */
  @action.bound
  public async startSession(): Promise<SessionStart> {
    if (!(await sharedSession.init({ hasSharedSession: this.sessionMode === 'shared' }))) {
      if (this.sessionMode === 'shared') {
        await this.clearSession();
      }
      return 'ready';
    }

    let result = await sharedSession.getToken({ timeoutMs: SESSION_START_TIMEOUT_MS });
    if (result.kind === 'unavailable') {
      await new Promise((resolve) => {
        setTimeout(resolve, SESSION_START_RETRY_MS);
      });
      result = await sharedSession.getToken({ timeoutMs: SESSION_START_TIMEOUT_MS });
    }

    if (result.kind === 'token') {
      // A stored session of another account ends first, with its workspace.
      if (isOtherAccount(result.idToken, this.email)) {
        await this.clearSession();
      }
      this.adoptSharedToken(result.idToken);
      // Without the user, the error page asks for a reload, and the session stays.
      return (await this.getUser()) ? 'ready' : 'unavailable';
    }
    if (result.kind === 'unavailable') {
      return 'unavailable';
    }
    const hasSession =
      this.loggedIn || this.sessionMode === 'shared' || !!this.sessionData.refreshToken;
    // A stored session ends at the switch. The login page tells the user that the session ended.
    const switched = hasSession && this.sessionMode === 'stored';
    if (hasSession) {
      await this.clearSession();
    }
    this.signInAgainAfterSwitch = switched;
    return 'ready';
  }

  private adoptSharedToken(idToken: string) {
    this.sessionMode = 'shared';
    this.setSessionToken({ idToken, refreshToken: '' });
  }

  @action.bound
  public setShowGetStarted(val: boolean) {
    this.showGetStarted = val;
  }

  @action.bound
  public setSsoOrgOptions(options: OrgOption[]) {
    this.ssoOrgOptions = options;
  }

  @action.bound
  public clearSsoOrgOptions() {
    this.ssoOrgOptions = [];
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @action.bound
  public setProvider(provider: Provider, providerUserId: string) {
    this.provider = provider;
    this.providerUserId = providerUserId;
  }

  @action.bound
  public setPhoneNumber(number: string) {
    this.phoneNumber = number;
  }

  @action.bound
  public setShowDatabricksWorkspace(val: boolean) {
    this.showDatabricksWorkspace = val;
  }

  @action.bound
  public setMfaStatus(value: MFAStatus) {
    this.mfaStatus = value;
  }

  @action.bound
  public signUp(name: string, organization: string, email?: string) {
    this.loggedIn = false;
    this.name = name;
    this.rootStore.organizationStore.name = organization;
    if (email) {
      this.email = email;
    }
  }

  /** `minValiditySeconds` asks the shared session for a token that stays valid at least that long. */
  @action.bound async setAndRefreshToken(idToken: string, minValiditySeconds?: number) {
    if (this.sessionMode === 'shared') {
      await this.refreshSharedToken(minValiditySeconds);
      return;
    }
    if (idToken !== this.token) {
      this.token = idToken;
    }
    let shouldSignOut = false;
    const refreshTokenAtStart = this.sessionData.refreshToken;
    // Gets current session. Tokens are refreshed by amplify automatically when they expire
    try {
      const res = await auth.currentSession(this.sessionData, this.email);
      // A sign-out during the wait cleared the session, so none of its tokens may come back.
      if (this.sessionData.refreshToken !== refreshTokenAtStart) {
        return;
      }

      if (res.status === 'success' && res.idToken) {
        this.token = res.idToken;
        this.sessionData.idToken = res.idToken;
        this.sessionData.refreshToken = res.refreshToken;
      }
    } catch (error: CatchErr) {
      if (error && error.response && error.response.status === 400) {
        shouldSignOut = true;
      }
    }

    // doing the same thing as error response interceptor
    if (shouldSignOut) {
      await this.signOutUser();
    }
  }

  /**
   * Gets the current ID token of the shared session. config-backend refreshes it only in its last minutes. An
   * ended session signs the user out here. When config-backend does not answer, the current token stays. When
   * another account signed in on the other site, the page reloads, so that no call of this account runs as the
   * other account.
   */
  private async refreshSharedToken(minValiditySeconds?: number) {
    const result = await sharedSession.getToken({ minValiditySeconds });
    // A sign-out during the wait ended this session, so none of its tokens may come back.
    if (this.sessionMode !== 'shared') {
      return;
    }
    if (result.kind === 'token') {
      const currentEmail = emailOf(this.token);
      if (currentEmail && isOtherAccount(result.idToken, currentEmail)) {
        await this.clearSession();
        window.location.reload();
        return;
      }
      this.setSessionToken({ idToken: result.idToken, refreshToken: '' });
    } else if (result.kind !== 'unavailable') {
      await this.signOutUser();
    }
  }

  /** Epoch ms when the current session token expires, or null when unknown. */
  getTokenExpirationTime(): number | null {
    // An unknown expiry is never treated as expired — the turn sends as usual.
    const exp = decodeTokenClaims(this.token)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  }

  @action.bound
  public setUserSessionForMFA(email: string, mfaData: IMfaData) {
    this.email = email;
    this.mfaData = mfaData;
    this.sessionData.idToken = mfaData.sessionToken as string;
  }

  @action.bound
  async updateEnforcedMfa() {
    const userData: { data: IUserData } = await apiAuthCaller({
      nonWorkspaceRoute: true,
      region: Region.US,
    }).get('/getUser');
    const { enforcedMfa } = userData.data;
    this.enforcedMfa = enforcedMfa;
  }

  @action.bound
  async loginUser(sessionData: CognitoTokens) {
    if (sharedSession.isEnabled()) {
      // The sign-in started a session at config-backend. The page uses only that session, never the sign-in's tokens.
      const shared = await sharedSession.getToken();
      if (shared.kind !== 'token' || !isSameAccount(shared.idToken, sessionData.idToken)) {
        await this.clearSession();
        throw new SharedSessionSignInError(sharedSignInFailureMessage(shared.kind));
      }
      this.adoptSharedToken(shared.idToken);
    } else {
      this.sessionMode = 'stored';
      this.setSessionToken(sessionData);
    }
    await this.getUser();
  }

  /** Signs in after Google or SSO. With the shared session, config-backend first starts a session from the tokens. */
  @action.bound
  async loginWithFederatedTokens(tokens: CognitoTokens) {
    if (!sharedSession.isEnabled()) {
      await this.loginUser(tokens);
      return;
    }
    const idToken = await sharedSession.startFederated(tokens);
    await this.loginUser({ idToken, refreshToken: tokens.refreshToken });
  }

  public async getUser(): Promise<boolean> {
    let loaded = false;
    try {
      const userData = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get<IUserData>('/getUser');

      const {
        id,
        name,
        email,
        phoneNumber,
        mfaStatus,
        enforcedMfa,
        workspaces,
        canSetupMfa,
        provider,
        providerUserId,
        organizations,
        pendingTasks = [],
        createdAt,
        updatedAt,
      } = userData.data;

      this.id = id;
      this.email = email;
      this.name = name;
      this.createdAt = createdAt;
      this.updatedAt = updatedAt;
      this.phoneNumber = phoneNumber;
      this.mfaStatus = mfaStatus;
      this.enforcedMfa = enforcedMfa;
      this.canSetupMfa = canSetupMfa;
      this.pendingTasks = pendingTasks;
      this.organizations = organizations;
      this.workspaces = workspaces;
      this.selectedWorkspaceId = this.getInitialWorkspaceId();
      this.saveCurrentWorkspace();
      // Preserve grace period / forced setup state across getUser() — addMfa page reads it
      if (
        this.mfaData?.state !== LoginMfaState.GracePeriod &&
        this.mfaData?.state !== LoginMfaState.ForcedSetup
      ) {
        this.mfaData = {};
      }
      this.loggedIn = true;
      loaded = true;
      this.setProvider(provider, providerUserId);

      const {
        selectedOrganizationId,
        selectedWorkspaceId,
        selectedWorkspace,
        selectedOrganization,
      } = this;

      await experiment.identify({
        id,
        workspaceId: selectedWorkspaceId,
        organizationId: selectedOrganizationId,
        planType: selectedOrganization?.planType,
        environment: selectedWorkspace?.environment,
      });

      this.identifyFoundation();

      bugsnagSetUser(this);

      // Feature flags are ready now that identify has resolved — load the sales
      // scheduling widget (Calendly or Qualified) based on the feature flag.
      // Reported rather than swallowed by the surrounding catch so a failure here
      // is visible without blocking the rest of the login flow.
      try {
        loadSchedulerWidget({ email: this.email, name: this.name });
      } catch (err) {
        webappClient.notify(err as Error);
      }

      // Also flag-gated, so it likewise has to wait for identify. Deliberately
      // not awaited: the SDK is fetched lazily over the network and the login
      // flow must not block on it (boot() reports its own failures).
      // Identified by email, not `id` — see the note on boot().
      customerIoInApp.boot(this.email);
    } catch (err) {
      // The result tells the callers that need it whether the user loaded.
    }
    return loaded;
  }

  public identifyFoundation(extra?: Partial<webappTypes.IdentifyTraits>) {
    const {
      id,
      email,
      name,
      createdAt,
      roles,
      rootStore,
      selectedOrganizationId,
      selectedWorkspaceId,
      selectedWorkspace,
      selectedWorkspaceRegion,
    } = this;
    const { organizationStore, billingStore, trialStore } = rootStore;
    webapp.identify(id, {
      email,
      environment: selectedWorkspace?.environment ?? 'PRODUCTION',
      isTrial: trialStore.hasActiveTrials,
      organizationId: selectedOrganizationId || '',
      planName: organizationStore.planName,
      region: selectedWorkspaceRegion,
      role: roles?.organization,
      userCreatedAt: createdAt,
      userName: name,
      workspaceId: selectedWorkspaceId,
      billingCycle: billingStore.currentBillingCycle,
      isInternal: email ? isRudderUser(email) : false,
      isGracePeriod: trialStore.isPlanTrialInGracePeriod,
      ...(trialStore.planTrial?.createdAt && { trialStartDate: trialStore.planTrial.createdAt }),
      ...(trialStore.planTrial?.endsAt && { trialEndDate: trialStore.planTrial.endsAt }),
      ...(trialStore.planTrial?.graceEndsAt && {
        gracePeriodEndDate: trialStore.planTrial.graceEndsAt,
      }),
      ...extra,
    });
  }

  private setSessionToken(session: CognitoTokens) {
    this.sessionData = session;
    this.token = this.sessionData.idToken;
  }

  trackOnboarding(traits: Traits) {
    this.identifyFoundation(traits);
    Analytics.track({ event: 'userCompletedOnboarding', properties: traits });
  }

  trackOnboardingV2(traits: TraitsV2) {
    const {
      onboardingQuestionsStatus,
      questionMigratingFrom,
      questionProductInterest,
      personaType,
      questionHowDidYouHear,
    } = traits;
    this.identifyFoundation({
      onboardingQuestionsStatus,
      questionMigratingFrom,
      questionProductInterest,
      questionHowDidYouHear,
      userCompanyRole: personaType ? [personaType] : undefined,
    });
    Analytics.track({
      event: 'userCompletedOnboarding',
      properties: (onboardingQuestionsStatus === 'skipped'
        ? { onboardingQuestionsStatus }
        : {
            onboardingQuestionsStatus,
            personaType,
            questionHowDidYouHear,
            questionProductInterest,
            questionMigratingFrom,
            userCompanyRole: personaType ? [personaType] : undefined,
          }) as Traits,
    });
  }

  setLoginMetadata({
    invited = false,
    firstTimeLogin = false,
  }: {
    invited?: boolean;
    firstTimeLogin?: boolean;
  }) {
    this.invitedUserFirstTimeLogin = firstTimeLogin && !!invited;
    this.firstTimeLogin = firstTimeLogin;

    if (this.firstTimeLogin) {
      Analytics.track({ event: 'ads_cnv_app_signup', properties: {} });
    }
  }

  public get accessibleWorkspaces() {
    return this.workspaces.filter((workspace) =>
      ACCESSIBLE_WORKSPACE_STATUSES.includes(workspace.status),
    );
  }

  @computed get accessibleOrganizations() {
    return this.organizations.filter(
      (organization) => organization.status !== OrgUserStatus.SUSPENDED,
    );
  }

  private getInitialWorkspaceId() {
    if (this.accessibleWorkspaces.length === 0) {
      return '';
    }

    const lastVisitedWorkspaceId = LocalStorage.get('lastVisitedWorkspace') || '';
    const lastVisitedWorkspace = this.accessibleWorkspaces.find(
      (workspace) => workspace.id === lastVisitedWorkspaceId,
    );

    if (lastVisitedWorkspace) {
      return lastVisitedWorkspace.id;
    }

    // No remembered workspace — default to the production one, falling back to
    // the last accessible workspace (e.g. a development-only organization).
    const productionWorkspace = this.accessibleWorkspaces.find(
      (workspace) => workspace.environment === 'PRODUCTION',
    );

    return (
      productionWorkspace?.id ?? this.accessibleWorkspaces[this.accessibleWorkspaces.length - 1].id
    );
  }

  removePendingTask(task: PendingTask) {
    const tasks = this.pendingTasks;
    this.pendingTasks = tasks.filter((t) => t !== task);
  }

  private async signOutFromBackend(globalSignout = false): Promise<void> {
    if (this.sessionMode === 'shared') {
      await this.signOutFromSharedSession(globalSignout);
      return;
    }
    try {
      await apiAuthCaller({
        idToken: this.sessionData.idToken,
        nonWorkspaceRoute: true,
        region: Region.US,
      })
        .ignore400()
        .post(
          '/signout',
          { refreshToken: this.sessionData.refreshToken },
          { params: { globalSignout } },
        );
    } catch (err) {}

    // With the flag on, this browser can have a shared session also while this page runs without it, for example
    // after a Flagsmith timeout. A Log out ends that session too. With the flag off, the page calls no session route.
    if (sharedSession.isFlagOn()) {
      try {
        await sharedSession.end({ timeoutMs: BEST_EFFORT_SIGN_OUT_TIMEOUT_MS });
      } catch (err) {
        // Best effort: a site that config-backend does not trust cannot end it.
      }
    }

    if (globalSignout) {
      await this.setAndRefreshToken(this.sessionData.idToken);
    }
  }

  /**
   * Ends the shared session. A global sign-out first ends every session of the user. As for `/signout`, a failure
   * does not stop the sign-out: these callers follow a change that already ended the user's sessions.
   */
  private async signOutFromSharedSession(globalSignout: boolean) {
    if (globalSignout) {
      try {
        await apiAuthCaller({
          idToken: this.sessionData.idToken,
          nonWorkspaceRoute: true,
          region: Region.US,
        })
          .ignore400()
          .post('/signout', {}, { params: { globalSignout } });
      } catch (err) {
        // Best effort: a change before this call already ended the user's sessions.
      }
    }
    try {
      await sharedSession.end();
    } catch (err) {
      // Best effort, for the same reason.
    }
  }

  @action.bound
  public async signOutUser(signOutfromBackend = false, globalSignout = false) {
    const endsSharedSession = signOutfromBackend && this.sessionMode === 'shared';
    if (signOutfromBackend) {
      await this.signOutFromBackend(globalSignout);
    }

    await this.clearSession();

    // The other tabs of this site sign out at once, and not at their next check.
    if (endsSharedSession) {
      announceSharedSignOut();
    }
  }

  /** Another tab signed out of the shared session. This tab signs out too, with no call to config-backend. */
  @action.bound
  public async endSessionFromAnotherTab() {
    if (this.sessionMode !== 'shared') {
      return;
    }
    await this.clearSession();
  }

  private async clearSession() {
    this.loggedIn = false;
    this.id = '';
    this.name = '';
    this.email = '';
    this.token = '';
    this.sessionData = {
      refreshToken: '',
      idToken: '',
    };
    this.provider = '';
    this.providerUserId = '';
    this.workspaces = [];
    this.selectedWorkspaceId = '';
    this.mfaData = undefined;
    this.phoneNumber = '';
    this.mfaStatus = 'NOMFA';
    this.enforcedMfa = false;
    this.canSetupMfa = false;
    this.sessionMode = 'stored';
    this.signInAgainAfterSwitch = false;

    this.resetStores();

    amplitudeEngagement.shutdown();

    customerIoInApp.shutdown();

    experiment.reset();

    bugsnagSetUser(this);
    try {
      await import('@aws-amplify/auth').then((res) => res.Auth.signOut());
    } catch (err) {}
    // Amplify signs out only after a sign-in page configured it. Before that, for example at the switch to the shared
    // session (ACT2-879), its Google and SSO tokens would stay in localStorage, so the page removes them itself.
    removeAmplifyTokens();
  }

  /**
   * User-initiated logout ("Log out" button) for federated (Hosted-UI) sessions.
   *
   * After the normal sign-out, redirects the browser through the Cognito
   * Hosted-UI logout endpoint to clear its session cookie — otherwise a federated
   * user can re-login within ~1h reusing the Cognito session, bypassing IdP-side
   * access removal. The logout URL is captured before `signOutUser` resets
   * `providerUserId`, and the redirect runs last since it navigates away.
   */
  @action.bound
  public async signOutUserAndClearHostedUiSession() {
    if (this.sessionMode === 'shared') {
      await this.signOutOfSharedSessionAndHostedUi();
      return;
    }

    const logoutUrl = this.isFederatedLogin ? buildCognitoLogoutUrl() : '';

    await this.signOutUser(true);

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  /**
   * The Log out of the session error page. While the flag is on, this browser can have a shared session also when this
   * page has a stored session or none, for example after a sign-in on Lookout. Only config-backend can end it, so it
   * ends first, and a failure throws before any local change, as the Log out of the shared session does.
   */
  @action.bound
  public async signOutFromSessionErrorPage() {
    if (this.sessionMode === 'shared' || !sharedSession.isFlagOn()) {
      await this.signOutUserAndClearHostedUiSession();
      return;
    }

    const { hostedUiSignOutUrl } = await sharedSession.end();
    const logoutUrl = hostedUiSignOutUrl ?? (this.isFederatedLogin ? buildCognitoLogoutUrl() : '');

    await this.signOutUser(true);
    announceSharedSignOut();

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  /**
   * The "Log out" button with the shared session. It fails closed: while config-backend keeps the session, the
   * next page would sign the user in again, and Lookout would stay signed in. So a failure throws before any
   * local change. config-backend names the Hosted UI sign-out page for Google and SSO users.
   */
  private async signOutOfSharedSessionAndHostedUi() {
    const { hostedUiSignOutUrl } = await sharedSession.end();
    const logoutUrl = hostedUiSignOutUrl ?? (this.isFederatedLogin ? buildCognitoLogoutUrl() : '');

    await this.clearSession();
    announceSharedSignOut();

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  private saveCurrentWorkspace() {
    LocalStorage.set('lastVisitedWorkspace', this.selectedWorkspaceId);
  }

  @action.bound
  public async switchWorkspace(workspaceId: string) {
    const { userStore } = this.rootStore;

    this.resetStores();
    this.selectedWorkspaceId = workspaceId;
    this.saveCurrentWorkspace();

    await experiment.identify({
      id: userStore.id,
      workspaceId: this.selectedWorkspaceId,
      organizationId: this.selectedOrganizationId,
      planType: this.selectedOrganization?.planType,
      environment: this.selectedWorkspace?.environment,
    });
  }

  /**
   * Ensures the production workspace of the selected organization is the active
   * one — used while the trial blocks the app so billing/upgrade actions target
   * the right workspace. Returns whether a switch was performed, so callers can
   * defer to the reload the switch triggers.
   */
  @action.bound
  public ensureProductionWorkspaceSelected(): boolean {
    const productionWorkspace = this.accessibleWorkspaces.find(
      (workspace) =>
        workspace.organizationId === this.selectedOrganizationId &&
        workspace.environment === 'PRODUCTION',
    );

    if (!productionWorkspace || productionWorkspace.id === this.selectedWorkspaceId) {
      return false;
    }

    this.switchWorkspace(productionWorkspace.id);
    return true;
  }

  @computed get selectedOrganizationId() {
    const workspace = this.workspaces.find(
      (workspace) => workspace.id === this.selectedWorkspaceId,
    );
    return workspace?.organizationId || '';
  }

  @computed get isOrgAdmin() {
    return this.roles?.organization === OrganizationRole.admin;
  }

  @computed get roles() {
    const member = this.rootStore.organizationStore.members.find(
      (member) => member.email === this.email,
    );
    return member?.roles!;
  }

  @computed get isSso() {
    return !!this.providerUserId && this.provider !== 'Google';
  }

  /**
   * True when the session was established through the Cognito Hosted UI
   * (federated login — SAML SSO or Google social), which is the only case that
   * leaves a Hosted-UI session cookie to clear on logout.
   */
  @computed get isFederatedLogin() {
    return !!this.providerUserId;
  }

  @computed get hasTotpEnabled() {
    return this.mfaStatus === 'TOTP';
  }

  @computed get isGoogleOrSso() {
    if (this.isSso) {
      return true;
    }

    const cognitoUsername = decodeTokenClaims(this.token)?.['cognito:username'];

    return typeof cognitoUsername === 'string' && cognitoUsername.startsWith('Google');
  }

  @computed get selectedWorkspaceRegion(): Region {
    const workspace = this.workspaces.find(
      (workspace) => workspace.id === this.selectedWorkspaceId,
    );
    return workspace?.defaultRegion || Region.US;
  }

  @computed get selectedWorkspace() {
    return this.workspaces.find((workspace) => workspace.id === this.selectedWorkspaceId);
  }

  @computed get selectedOrganization() {
    return this.organizations.find(
      (organization) => organization.id === this.selectedOrganizationId,
    );
  }

  @computed get isUserSuspendedInSelectedOrganization() {
    return this.selectedOrganization?.status === OrgUserStatus.SUSPENDED;
  }

  @action.bound
  private resetStores() {
    const {
      billingStore,
      sourcesListStore,
      destinationsListStore,
      transformationsListStore,
      librariesListStore,
      trackingPlanListStore,
      modelListStore,
      audienceListStore,
      profilesListStore,
      workspaceStore,
      organizationStore,
      featuresStore,
      settingsStore,
      retlSourceListStore,
      retlConnectionListStore,
      credentialStore,
      serviceAccessTokenStore,
    } = this.rootStore;

    workspaceStore.reset();
    organizationStore.reset();
    settingsStore.reset();
    billingStore.reset();
    featuresStore.reset();
    sourcesListStore.reset();
    destinationsListStore.reset();
    transformationsListStore.reset();
    librariesListStore.reset();
    trackingPlanListStore.reset();
    modelListStore.reset();
    audienceListStore.reset();
    profilesListStore.reset();
    retlSourceListStore.reset();
    retlConnectionListStore.reset();
    credentialStore.reset();
    serviceAccessTokenStore.reset();
  }
}
